import { Modal, Spin } from "antd";
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { StarIcon } from "../../assets/svg";
import { Timer } from "../../components/timer";
import { APIStatus } from "../../constants";
import { Locations } from "../../constants/locations";
import { useAppDispatch, useAppSelector } from "../../modal/hooks";
import { ExamResponse } from "../../modal/question";
import {
	answers,
	draftExam,
	examPayload,
	failedExam,
	getAllQuestions,
	getRescheduledQuestions,
	submitExam,
} from "../../services/exam/exam.service";
import { clearGetAllQuestions, clearGetRescheduledQuestions, clearSubmitExam } from "../../services/exam/exam.slice";
import "./exam.scss";

export const Exam: React.FunctionComponent = () => {
	const reScheduledExam = useRef(JSON.parse(sessionStorage.getItem("reScheduledExam") || "false"));

	let studentId: number | null = null;
	let token: any = "";
	const params = useParams();
	const studentInfo = JSON.parse(sessionStorage.getItem("studentInfo") || "{}");
	studentId = studentInfo ? parseInt(studentInfo.id) : null;
	token = params.token;

	const getAllQuestionsCompleted = useAppSelector((store) => store.exam.getAllQuestionsCompleted);
	const getRescheduledQuestionsCompleted = useAppSelector((store) => store.exam.getRescheduledQuestionsCompleted);
	const submitExamCompleted = useAppSelector((store) => store.exam.submitExamCompleted);
	const questionDetails = useAppSelector((store) => store.exam.questionDetails);
	const studentsDetails = useAppSelector((store) => store.studentsRegistration.studentsDetails);
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(0);
	const [questions, setQuestions] = useState<ExamResponse>({
		questions: [],
		timeLeft: 0,
		name: "",
		studentId: 0,
		email: "",
	});
	const [timeOutModalOpen, setTimeOutModalOpen] = useState<boolean>(false);
	const [submitModalOpen, setSubmitModalOpen] = useState<boolean>(false);
	const [loading, setLoading] = useState<boolean>(false);

	useEffect(() => {
		// Define the function to handle page reload or tab close
		const handleBeforeUnload = (event: BeforeUnloadEvent) => {
			alert("You attempted to reload or leave the page. The exam has been failed.");
			// Set the returnValue to display a confirmation dialog before leaving
			event.preventDefault();
			event.returnValue = ""; // This is required for some browsers to trigger the dialog.
		};
		window.addEventListener("beforeunload", handleBeforeUnload);
		// Cleanup function to remove the event listeners
		return () => {
			window.removeEventListener("beforeunload", handleBeforeUnload);
		};
	}, []);

	useEffect(() => {
		if (reScheduledExam.current) {
			dispatch(getRescheduledQuestions(studentId));
			setLoading(true);
		} else {
			dispatch(getAllQuestions());
			setLoading(true);
		}
		// const isExamActive = localStorage.getItem("examActive");
		// if (isExamActive) {
		// 	console.log("isExamActive detected");
		// 	navigate(Locations.FAIL_PAGE, { state: { reason: " isExamActive ", studentId: studentId } });
		// } else {
		// 	localStorage.setItem("examActive", "true");
		// }
	}, []);

	const handleNext = () => {
		if (currentQuestionIndex < questions?.questions.length - 1) {
			setCurrentQuestionIndex(currentQuestionIndex + 1);
		}
		submitClicked(true);
	};

	const handlePrev = () => {
		if (currentQuestionIndex > 0) {
			setCurrentQuestionIndex(currentQuestionIndex - 1);
		}
		submitClicked(true);
	};

	useEffect(() => {
		const handleSideKey = (event: KeyboardEvent) => {
			switch (event.key) {
			case "ArrowLeft":
				handlePrev();
				break;
			case "ArrowRight":
				handleNext();
				break;
			default:
				break;
			}
		};
		window.addEventListener("keydown", handleSideKey);
		return () => {
			window.removeEventListener("keydown", handleSideKey);
		};
	}, [currentQuestionIndex, handleNext, handlePrev]);

	useEffect(() => {
		const handlePopstate = () => {
			if (localStorage.getItem("isBackNavigate") === "true") {
				// const studentId = localStorage.getItem("studentsDetails");
				// const inNumber = parseInt(studentId ? studentId : "");
				console.log("back detected");
				const payload = {
					id: studentId,
					token: token ?? "",
				};
				if (!reScheduledExam.current) {
					dispatch(failedExam(payload));
				}
				navigate(Locations.FAIL_PAGE, { state: { reason: " back navigation ", studentId: studentId } });
			}
		};
		window.addEventListener("popstate", handlePopstate);
		localStorage.setItem("isBackNavigate", "true");
	}, []);

	// Detecting tab switch
	useEffect(() => {
		const handleVisibilityChange = () => {
			if (document.visibilityState === "hidden") {
				if (studentId) {
					const payload = {
						id: studentId,
						token: token ?? "",
					};
					if (!reScheduledExam.current) {
						dispatch(failedExam(payload));
					}
					navigate(Locations.FAIL_PAGE, { state: { reason: " detected tab switch", studentId: studentId } });
				}
			}
		};

		document.addEventListener("visibilitychange", handleVisibilityChange);

		return () => {
			document.removeEventListener("visibilitychange", handleVisibilityChange);
		};
	}, []);

	// Detecting screen resizing (Malpractice case)
	useEffect(() => {
		const handleScreenViolation = () => {
			if (window.outerWidth < screen.availWidth * 0.9 || window.outerHeight < screen.availHeight * 0.9) {
				const payload = {
					id: studentId,
					token: token ?? "",
				};
				if (!reScheduledExam.current) {
					dispatch(failedExam(payload));
				}
				navigate(Locations.FAIL_PAGE, { state: { reason: " screen resizing detected", studentId: studentId } });
			}
		};

		window.addEventListener("resize", handleScreenViolation);
		return () => {
			window.removeEventListener("resize", handleScreenViolation);
		};
	}, []);

	useEffect(() => {
		if (studentsDetails.id) {
			localStorage.setItem("studentsDetails", studentsDetails.id.toString());
		}
	}, [studentsDetails.id]);

	useEffect(() => {
		if (localStorage.getItem("isReloaded") === "true") {
			const payload = {
				id: studentId,
				token: token ?? "",
			};
			if (!reScheduledExam.current) {
				dispatch(failedExam(payload));
			}
			navigate(Locations.FAIL_PAGE, { state: { reason: " screen reload ", studentId: studentId } });
		}
		const handleBeforeUnload = (event: any) => {
			event.preventDefault();
			localStorage.setItem("isReloaded", "true");
		};
		window.addEventListener("beforeunload", handleBeforeUnload);
		return () => {
			window.removeEventListener("beforeunload", handleBeforeUnload);
		};
	}, []);

	useEffect(() => {
		if (getAllQuestionsCompleted === APIStatus.FULFILLED) {
			console.log("getAllQuestionsCompletedUseEffect");
			setQuestions(JSON.parse(JSON.stringify(questionDetails)));
			setLoading(false);
			dispatch(clearGetAllQuestions());
		}
		if (getAllQuestionsCompleted === APIStatus.REJECTED) {
			setLoading(false);
			dispatch(clearGetAllQuestions());
		}
	}, [getAllQuestionsCompleted]);

	useEffect(() => {
		if (getRescheduledQuestionsCompleted === APIStatus.FULFILLED) {
			console.log("getRescheduledQuestionsCompletedUseEffect");
			setQuestions(JSON.parse(JSON.stringify(questionDetails)));
			setLoading(false);
			dispatch(clearGetRescheduledQuestions());
		}
		if (getRescheduledQuestionsCompleted === APIStatus.REJECTED) {
			setLoading(false);
			dispatch(clearGetRescheduledQuestions());
		}
	}, [getRescheduledQuestionsCompleted]);

	useEffect(() => {
		if (submitExamCompleted === APIStatus.FULFILLED) {
			setLoading(false);
			navigate(Locations.END_PAGE);
			dispatch(clearSubmitExam());
		}
		if (submitExamCompleted === APIStatus.REJECTED) {
			setLoading(false);
			dispatch(clearSubmitExam());
		}
	}, [submitExamCompleted]);

	const handleTimerEnd = () => {
		setTimeOutModalOpen(true);
	};

	const handleOptionClick = (optionId: number) => {
		const tempQuestions = [...questions.questions];
		const currentQuestion = tempQuestions[currentQuestionIndex];
		if (currentQuestion.selectedOption === optionId) {
			tempQuestions[currentQuestionIndex] = { ...tempQuestions[currentQuestionIndex], selectedOption: null };
		} else {
			tempQuestions[currentQuestionIndex] = { ...tempQuestions[currentQuestionIndex], selectedOption: optionId };
		}
		setQuestions((prestate) => ({ ...prestate, questions: [...tempQuestions] }));
	};

	const handleSubmit = () => {
		setSubmitModalOpen(true);
	};

	const submitClicked = (autoSave = false) => {
		const answersPayload: answers[] = questions?.questions.map((el) => ({
			questionId: el.id,
			selectedOptionId: el.selectedOption || null,
		}));
		const payload: examPayload = {
			studentId: studentsDetails.id ?? questions.studentId,
			examToken: `${token}`,
			questions: answersPayload,
		};

		if (autoSave) {
			payload.questions = payload.questions.filter((el) => el.selectedOptionId !== null);
			const prevAnswers = JSON.parse(sessionStorage.getItem("prevAnswers") || "[]");

			if (JSON.stringify(prevAnswers) !== JSON.stringify(payload.questions)) {
				sessionStorage.setItem("prevAnswers", JSON.stringify(payload.questions));
				dispatch(draftExam(payload));
			}
			return;
		} else {
			dispatch(submitExam(payload));
			setLoading(true);
			setSubmitModalOpen(false);
			setTimeOutModalOpen(false);
			localStorage.removeItem("prevAnswers");
		}
	};

	const cancelClicked = () => {
		setSubmitModalOpen(false);
	};

	return (
		<div className="student_module_overview_main_container main_outer_bg d-flex flex-column h-100">
			<div className="logo_container">
				<img className="logo_img mr-2" src="/images/logo.png" />
			</div>
			<div className="student_module_overview_content_box flex_1_1_10 p-4">
				<div className="student_module_overview_content_box_content">
					<div className="student_exam_container d-flex flex-column">
						<div className="w-100 d-flex">
							<div className="w-50 d-flex align-items-end justify-content-between font_16">
								<div className="word_break">
									<div className="font_medium">Hi, {studentsDetails.name ?? questions?.name}</div>
									<div>{studentsDetails.email ?? questions?.email}</div>
								</div>
								<div className="pt-1">
									<span>Question</span>
									<span className="pl-2 font_bold">
										{currentQuestionIndex + 1}/{questions?.questions?.length}
									</span>
									<div className="weightage d-flex align-items-center justify-content-center">
										{questions?.questions?.[currentQuestionIndex] && (
											<div className="weightage d-flex align-items-center justify-content-center">
												<span>
													{questions?.questions[currentQuestionIndex].weightage && (
														<>
															{[...Array(questions?.questions[currentQuestionIndex]?.maxWeightage)].map((_, index) => (
																<span key={index} className="pl-1" style={{ marginRight: "2px" }}>
																	<StarIcon
																		color={
																			index < questions?.questions[currentQuestionIndex]?.weightage
																				? "#f7ba02"
																				: "#E8E8E8"
																		}
																		width={16}
																		height={16}
																	/>
																</span>
															))}
														</>
													)}
												</span>
											</div>
										)}
									</div>
								</div>
							</div>
							<div className="w-50 text-right">
								{questions?.timeLeft && (
									<Timer handleTimerEnd={handleTimerEnd} loading={loading} time={questions?.timeLeft} />
								)}
							</div>
						</div>
						<div className="student_exam_content_box overview_outer_border_box flex_1_1_10 overflow-auto mt-3 font_15">
							{questions?.questions?.length === 0 ? (
								<div className="d-flex align-items-center justify-content-center h-100">
									<Spin />
								</div>
							) : (
								<div>
									<div className="exam_question_box_container">
										{questions?.questions?.length > 0 && (
											<>
												{" "}
												<div className="d-flex">
													<div className="mr-1 font_bold">{currentQuestionIndex + 1}.</div>
													<div className="word_break">{questions?.questions[currentQuestionIndex].question}</div>
												</div>
												{questions?.questions[currentQuestionIndex].questionMedia ? (
													<div className="exam_img_attachment_container my-2">
														<img src={questions?.questions[currentQuestionIndex].questionMedia} />
													</div>
												) : (
													""
												)}
											</>
										)}
									</div>
									<div className="exam_answer_box_container px-3">
										{questions?.questions?.length &&
											questions?.questions[currentQuestionIndex].options?.length &&
											questions?.questions[currentQuestionIndex].options.map((option, index) => (
												<div
													key={index}
													className={
														option.id === questions?.questions[currentQuestionIndex].selectedOption
															? "exam_answer_box answer"
															: "exam_answer_box"
													}
													onClick={() => handleOptionClick(option.id)}
												>
													<div className="d-flex">
														<div className="mr-1 font_bold">{index + 1}.</div>
														<div className="word_break">{option.option}</div>
													</div>
													{option.optionMedia ? (
														<div className="exam_img_attachment_container py-2">
															<img src={option.optionMedia} />
														</div>
													) : (
														""
													)}
												</div>
											))}
									</div>
								</div>
							)}
						</div>
					</div>
					<div className="footer_section d-flex justify-content-end px-3 align-items-baseline">
						<div className="mt-2 d-flex align-items-center justify-content-end mb-4">
							<button
								className="secondary_border_btn font_16 min_height_36 min_width_130"
								onClick={handlePrev}
								disabled={currentQuestionIndex === 0 || loading}
							>
								Previous
							</button>
							<button
								className="primary_btn forward_btn font_16 min_height_36 min_width_130 ml-2"
								onClick={handleNext}
								disabled={currentQuestionIndex === (questions?.questions?.length ?? 0) - 1 || loading}
							>
								Next
							</button>
							<button
								className="primary_btn font_16 min_height_36 min_width_130 ml-2"
								onClick={handleSubmit}
								disabled={loading}
							>
								Submit All
							</button>
						</div>
					</div>
				</div>
			</div>
			{timeOutModalOpen ? (
				<div>
					<Modal
						getContainer={false}
						title="Time Out"
						open={true}
						closable={false}
						footer={[
							<div key={"sprint_footer"} className="d-flex align-items-center justify-content-end mr-3">
								<button className="primary_btn min_width_120 ml-2 mr-1" onClick={() => submitClicked()}>
									Close
								</button>
							</div>,
						]}
					>
						<div className="px-4">
							The exam has ended as the allotted time has expired. All the attended answers will be submitted.
						</div>
					</Modal>
				</div>
			) : (
				""
			)}
			{submitModalOpen ? (
				<div>
					<Modal
						getContainer={false}
						title="Submit Answers"
						open={true}
						closable={false}
						footer={[
							<div key={"sprint_footer"} className="d-flex align-items-center justify-content-end mr-3">
								<button className="secondary_border_btn min_width_120" onClick={cancelClicked}>
									No
								</button>
								<button
									className="primary_btn min_width_120 ml-2 mr-1"
									onClick={() => {
										submitClicked(false);
									}}
								>
									Yes
								</button>
							</div>,
						]}
					>
						<div className="px-4 font_regular">Are you sure to submit all your answers and complete the exam ?</div>
					</Modal>
				</div>
			) : (
				""
			)}
		</div>
	);
};
